/**
 * web/src/games/nocturne/engine.ts — the Nocturne game engine.
 *
 * Ported from the design pack's reference client and made pure + typed: every
 * applier takes a game and returns a NEW game (or null when the intent is
 * invalid), so the Case Host device applies intents and the guest renders only
 * from broadcast public state. Nothing here touches the network, storage, or
 * the DOM — the room is a pipe and this file is the rules.
 *
 * Source of truth: /pack/nocturne-game-design-specification.md (clue geometry,
 * constants, both modes, win conditions).
 */
import type { NocturneIconKey } from './icons';

/* ── The City ─────────────────────────────────────────────────────── */

export interface LocDef {
  name: string;
  icon: NocturneIconKey;
}

export const LOCS: readonly LocDef[] = [
  { name: 'The Velvet Bar', icon: 'velvet-bar' },
  { name: 'The Jazz Cellar', icon: 'jazz-cellar' },
  { name: 'Rooftop Garden', icon: 'rooftop-garden' },
  { name: "Maison d'Art", icon: 'gallery' },
  { name: 'The Grand Hotel', icon: 'grand-hotel' },
  { name: 'The Old Docks', icon: 'old-docks' },
  { name: 'Lamplight Bookshop', icon: 'bell-book' },
  { name: 'Moonlit Park', icon: 'vaux-park' },
  { name: 'The Gilded Theatre', icon: 'meridian' },
];

/** Orthogonal neighbours per location (3×3 grid, index = row*3+col). */
export const ADJ: readonly number[][] = [
  [1, 3], [0, 2, 4], [1, 5],
  [0, 4, 6], [1, 3, 5, 7], [2, 4, 8],
  [3, 7], [4, 6, 8], [5, 7],
];

/* ── Constants (spec §3 / §4) ─────────────────────────────────────── */

export const ROUNDS = 4;
export const AP_DAY = 2;
export const INTER_COST = 2;
export const COVER_MAX = 3;
export const FALSE_MAX = 2;
export const GUARD_MAX = 2;
export const ESCAPE_MAX = 3;

/** Duet night decks — composition is public (fair deduction), order is not. */
export const DECK_FIRST: readonly NightAct[] = ['flee', 'flee', 'cover', 'lead', 'low'];
export const DECK_CITY: readonly NightAct[] = ['flee', 'flee', 'flee', 'cover', 'cover', 'lead'];

export function deckFor(difficulty: Difficulty): readonly NightAct[] {
  return difficulty === 'first' ? DECK_FIRST : DECK_CITY;
}

/* ── Types ────────────────────────────────────────────────────────── */

export type Mode = 'duel' | 'duet';
export type Difficulty = 'first' | 'city';
export type NightAct = 'flee' | 'cover' | 'lead' | 'low';
export type DayAction = 'canvass' | 'interrogate' | 'guard' | 'accuse' | 'end_day';
export type Phase = 'night' | 'day' | 'over';
export type Seat = 'A' | 'B';
export type Winner = 'phantom' | 'detective';
export type JournalTone = 'gold' | 'rose' | 'slate' | 'star';

export interface JournalEntry {
  round: number;
  text: string;
  tone: JournalTone;
}
export interface NightEntry {
  round: number;
  text: string;
}

export interface Game {
  crimeLoc: number;
  falseLeads: number[];
  cover: number[];
  coverUses: number;
  falseUses: number;
  escape: number;
  round: number;
  ap: number;
  guardsUsed: number;
  accusedUsed: boolean;
  winner: Winner | null;
  reason: string;
  marksCanvass: (boolean | null)[];
  marksInter: (number | null)[];
  journal: JournalEntry[];
  nightLog: NightEntry[];
  deck: NightAct[];
  deckIdx: number;
  phase: Phase;
  /** Duet: which seat acts first today (alternates day to day). */
  firstSeat: Seat;
}

export interface RevealState {
  crimeLoc: number;
  cover: number[];
  falseLeads: number[];
  nightLog: NightEntry[];
}

/** The authoritative snapshot the Case Host broadcasts after every mutation.
 *  Hidden state ships only when the game is over (`reveal`). */
export interface PublicState {
  v: 1;
  mode: Mode;
  round: number;
  phase: Phase;
  ap: number;
  escape: number;
  guardsUsed: number;
  accusedUsed: boolean;
  turn: Seat | null;
  marksCanvass: (boolean | null)[];
  marksInter: (number | null)[];
  journal: JournalEntry[];
  winner: Winner | null;
  reason: string;
  reveal?: RevealState;
}

export interface DayIntent {
  action: DayAction;
  loc?: number;
  by?: Seat;
}

/** A deterministic RNG for tests and deck shuffles (spec §6: mulberry32). */
export type Rng = () => number;

export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function shuffle<T>(arr: readonly T[], rng: Rng = Math.random): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = out[i];
    out[i] = out[j];
    out[j] = tmp;
  }
  return out;
}

/* ── Engine ───────────────────────────────────────────────────────── */

export function newGame(crimeLoc: number, deck: NightAct[] = []): Game {
  return {
    crimeLoc,
    falseLeads: [],
    cover: Array(9).fill(0),
    coverUses: 0,
    falseUses: 0,
    escape: 0,
    round: 1,
    ap: AP_DAY,
    guardsUsed: 0,
    accusedUsed: false,
    winner: null,
    reason: '',
    marksCanvass: Array(9).fill(null),
    marksInter: Array(9).fill(null),
    journal: [],
    nightLog: [],
    deck: [...deck],
    deckIdx: 0,
    phase: 'night',
    firstSeat: 'A',
  };
}

/** Clue geometry (spec §2): the scene reads ★★★, orthogonal neighbours ★★,
 *  everything else cold; a false lead reads ★★★; each cover on L subtracts one
 *  star (floor 0). */
export function tierAt(g: Game, loc: number): number {
  let base = loc === g.crimeLoc ? 3 : ADJ[g.crimeLoc].includes(loc) ? 2 : 0;
  if (g.falseLeads.includes(loc)) base = 3;
  return Math.max(0, base - g.cover[loc]);
}

export const TIER_TEXT: readonly string[] = [
  'the trail is cold.',
  '★ fading traces.',
  '★★ someone passed through.',
  '★★★ a witness got a clear look.',
];

export function cloneGame(g: Game): Game {
  return {
    ...g,
    cover: [...g.cover],
    falseLeads: [...g.falseLeads],
    marksCanvass: [...g.marksCanvass],
    marksInter: [...g.marksInter],
    journal: [...g.journal],
    nightLog: [...g.nightLog],
    deck: [...g.deck],
  };
}

export function otherSeat(s: Seat): Seat {
  return s === 'A' ? 'B' : 'A';
}

/** Duet: whose action is due right now (null when the day is spent). */
export function turnOf(g: Game, mode: Mode): Seat | null {
  if (mode !== 'duet') return null;
  if (g.ap >= AP_DAY) return g.firstSeat;
  if (g.ap >= 1) return otherSeat(g.firstSeat);
  return null;
}

/** Authoritative public snapshot — hidden state only after game over. */
export function toPublicState(g: Game, mode: Mode): PublicState {
  const over = g.phase === 'over';
  return {
    v: 1,
    mode,
    round: g.round,
    phase: g.phase,
    ap: g.ap,
    escape: g.escape,
    guardsUsed: g.guardsUsed,
    accusedUsed: g.accusedUsed,
    turn: turnOf(g, mode),
    marksCanvass: [...g.marksCanvass],
    marksInter: [...g.marksInter],
    journal: [...g.journal],
    winner: g.winner,
    reason: g.reason,
    reveal: over
      ? {
          crimeLoc: g.crimeLoc,
          cover: [...g.cover],
          falseLeads: [...g.falseLeads],
          nightLog: [...g.nightLog],
        }
      : undefined,
  };
}

/* ── Mutating appliers (each operates on a fresh clone) ───────────── */

function applyCanvass(ng: Game, loc: number, by?: string): void {
  const hot = tierAt(ng, loc) >= 2;
  ng.marksCanvass[loc] = hot;
  ng.ap -= 1;
  const base = hot
    ? `Canvassed ${LOCS[loc].name} — whispers of a stranger.`
    : `Canvassed ${LOCS[loc].name} — nothing but rain.`;
  ng.journal.push({ round: ng.round, text: by ? `${by} — ${base}` : base, tone: hot ? 'rose' : 'slate' });
}

function applyInterrogate(ng: Game, loc: number, by?: string): void {
  const t = tierAt(ng, loc);
  ng.marksInter[loc] = t;
  if (t >= 2) ng.marksCanvass[loc] = true;
  ng.ap -= INTER_COST;
  const base = `Interrogated ${LOCS[loc].name} — ${TIER_TEXT[t]}`;
  ng.journal.push({ round: ng.round, text: by ? `${by} — ${base}` : base, tone: t >= 2 ? 'star' : 'slate' });
}

function applyGuard(ng: Game, by?: string): void {
  ng.escape = Math.max(0, ng.escape - 1);
  ng.guardsUsed += 1;
  ng.ap -= 1;
  const base = `Tightened the city gates — the Phantom's escape was set back.`;
  ng.journal.push({ round: ng.round, text: by ? `${by} — ${base}` : base, tone: 'gold' });
}

/** The Duet Phantom's night card, with the spec's heuristics: cover targets
 *  uncanvassed streets adjacent to the scene; false leads target uncanvassed
 *  streets far from it. */
export function applyDuetNight(ng: Game, rng: Rng = Math.random): void {
  const card = ng.deck[ng.deckIdx];
  ng.deckIdx += 1;
  let text: string;
  if (card === 'flee') {
    ng.escape += 1;
    text = 'The Phantom slipped toward the city gates.';
    if (ng.escape >= ESCAPE_MAX) {
      ng.winner = 'phantom';
      ng.reason = 'escape';
    }
  } else if (card === 'cover') {
    const ring = ADJ[ng.crimeLoc];
    const fresh = ring.filter((l) => ng.cover[l] < 2 && ng.marksCanvass[l] === null);
    const pool = fresh.length ? fresh : ring.filter((l) => ng.cover[l] < 2);
    if (pool.length) {
      const t = pool[Math.floor(rng() * pool.length)];
      ng.cover[t] += 1;
      text = `The Phantom covered their tracks at ${LOCS[t].name}.`;
    } else {
      text = 'The Phantom paced in the shadows.';
    }
  } else if (card === 'lead') {
    const aura = new Set([ng.crimeLoc, ...ADJ[ng.crimeLoc]]);
    const farFresh: number[] = [];
    const far: number[] = [];
    for (let i = 0; i < 9; i++) {
      if (aura.has(i) || ng.falseLeads.includes(i)) continue;
      if (ng.marksCanvass[i] === null) farFresh.push(i);
      else far.push(i);
    }
    const pool = farFresh.length ? farFresh : far;
    if (pool.length) {
      const t = pool[Math.floor(rng() * pool.length)];
      ng.falseLeads.push(t);
      text = `The Phantom planted a false story at ${LOCS[t].name}.`;
    } else {
      text = 'The Phantom paced in the shadows.';
    }
  } else {
    text = 'The Phantom lay low — the night passed in silence.';
  }
  ng.nightLog.push({ round: ng.round, text });
}

/* ── Public appliers — validate, then return the next game (or null) ─ */

function finishGame(ng: Game, winner: Winner, reason: string): Game {
  ng.winner = winner;
  ng.reason = reason;
  ng.phase = 'over';
  return ng;
}

/** The Phantom's night move (Duel, host device). Returns null when invalid. */
export function applyNightAction(g: Game, act: NightAct, target: number | null): Game | null {
  if (g.phase !== 'night' || g.winner) return null;
  const ng = cloneGame(g);
  let text: string;
  if (act === 'flee') {
    ng.escape += 1;
    text = 'The Phantom slipped toward the city gates.';
    if (ng.escape >= ESCAPE_MAX) return finishGame(ng, 'phantom', 'escape');
  } else if (act === 'cover') {
    if (target === null || ng.coverUses >= COVER_MAX) return null;
    ng.cover[target] += 1;
    ng.coverUses += 1;
    text = `The Phantom covered their tracks at ${LOCS[target].name}.`;
  } else if (act === 'lead') {
    if (target === null || ng.falseUses >= FALSE_MAX) return null;
    ng.falseLeads.push(target);
    ng.falseUses += 1;
    text = `The Phantom planted a false story at ${LOCS[target].name}.`;
  } else {
    text = 'The Phantom lay low — the night passed in silence.';
  }
  ng.nightLog.push({ round: ng.round, text });
  ng.phase = 'day';
  return ng;
}

/** The Case Host validates + applies every day intent (its own and the
 *  partner's). `labelFor` names the acting seat in the journal. Returns null
 *  for an invalid intent (wrong phase, AP, budget, or Duet turn). */
export function applyDayIntent(
  g: Game,
  mode: Mode,
  intent: DayIntent,
  labelFor?: (seat: Seat) => string,
  rng: Rng = Math.random,
): Game | null {
  if (g.phase !== 'day' || g.winner) return null;
  const turn = turnOf(g, mode);
  const actorSeat = intent.by ?? null;
  const label = (seat: Seat | null): string | undefined =>
    seat && labelFor ? labelFor(seat) : undefined;

  switch (intent.action) {
    case 'canvass': {
      if (intent.loc === undefined || g.ap < 1) return null;
      if (mode === 'duet' && actorSeat !== turn) return null;
      const ng = cloneGame(g);
      applyCanvass(ng, intent.loc, label(actorSeat));
      return ng;
    }
    case 'interrogate': {
      if (intent.loc === undefined || g.ap < INTER_COST) return null;
      if (mode === 'duet' && actorSeat !== turn) return null;
      const ng = cloneGame(g);
      const who =
        mode === 'duet' && turn
          ? `${label(turn) ?? turn} & ${label(otherSeat(turn)) ?? otherSeat(turn)}`
          : label(actorSeat);
      applyInterrogate(ng, intent.loc, who);
      return ng;
    }
    case 'guard': {
      if (g.ap < 1 || g.guardsUsed >= GUARD_MAX) return null;
      if (mode === 'duet' && actorSeat !== turn) return null;
      const ng = cloneGame(g);
      applyGuard(ng, label(actorSeat));
      return ng;
    }
    case 'accuse': {
      if (intent.loc === undefined || g.accusedUsed) return null;
      const ng = cloneGame(g);
      ng.accusedUsed = true;
      return intent.loc === ng.crimeLoc
        ? finishGame(ng, 'detective', 'right-accuse')
        : finishGame(ng, 'phantom', 'wrong-accuse');
    }
    case 'end_day': {
      const ng = cloneGame(g);
      if (ng.round >= ROUNDS) return finishGame(ng, 'phantom', 'timeout');
      ng.round += 1;
      ng.ap = AP_DAY;
      if (mode === 'duet') {
        ng.firstSeat = otherSeat(ng.firstSeat);
        applyDuetNight(ng, rng);
        if (ng.winner) return finishGame(ng, ng.winner, ng.reason || 'escape');
      }
      ng.phase = 'night';
      return ng;
    }
    default:
      return null;
  }
}

/** Start a Duet case on the host device: random scene, shuffled public-composition
 *  deck, first night card already drawn. */
export function startDuetGame(difficulty: Difficulty, rng: Rng = Math.random): Game {
  const ng = newGame(Math.floor(rng() * 9), shuffle(deckFor(difficulty), rng));
  applyDuetNight(ng, rng);
  return ng;
}

/** Endgame copy (spec §5). */
export function reasonTextFor(reason: string, crimeLoc: number, coop: boolean): string {
  if (reason === 'escape') {
    return 'Three flights in the rain. The Phantom left the city, and the secret left with them.';
  }
  if (reason === 'right-accuse') {
    return `The accusation was true. The Phantom was unmasked at ${LOCS[crimeLoc].name}.`;
  }
  if (reason === 'wrong-accuse') {
    return coop
      ? 'The accusation was wrong. The trail dissolved, and the Phantom vanished.'
      : 'The accusation was wrong. The city laughed, the trail dissolved, and the Phantom vanished.';
  }
  return 'Four days of rain washed the trail away. The Phantom was gone by morning.';
}
